import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateAgentDialog } from '@/pages/Agents/AgentCreatePage/components/create-agent-dialog';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DataTable } from '@/components/ui/data-table';

import { useEventHandler } from '@/hooks/use-event-handler';
import { useToggle } from '@/hooks/use-toggle';

import { useAgentsQuery } from '@/query/agent.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { AgentSearchAndFilter } from './components/agent-header';
import { getColumns } from './components/columns';
import { AgentEmptyResult } from './components/empty';

import { useAgentsListStore } from '@/store/agents-list.store';
import type { Agent } from '@/types/agent.type';
import { type ColumnDef } from '@tanstack/react-table';

export default function Agents() {
  const { state, toggle, open } = useToggle();

  const { currentPage, pageSize, query, setCurrentPage, setPageSize, sort, setSort, filters } =
    useAgentsListStore();

  const { data, isLoading } = useAgentsQuery({
    page: currentPage,
    page_size: pageSize,
    query,
    filters,
  });

  const navigate = useNavigate();
  useEventHandler({
    name: 'agents:create',
    eventHandler: open,
  });

  const agents: Agent[] = data?.data || [];
  const meta = data?.meta;

  const columns = useMemo<ColumnDef<Agent>[]>(() => getColumns(), [agents]);

  return (
    <>
      <div className="flex flex-col h-full space-y-6">
        {/** Search and Filter */}
        <AgentSearchAndFilter agents={agents} />

        <div className="bg-background border rounded-lg">
          <DataTable
            columns={columns}
            data={agents}
            sorting={sort}
            isLoading={isLoading}
            onSortingChange={setSort}
            onRowClick={row => {
              navigate(`/agents/${row.canonical_name}`);
            }}
            emptyState={<AgentEmptyResult />}
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Create Agent Dialog */}
      <ProtectedComponent requiredPermissions={[Permissions.AgentCreateUpdate]}>
        {state && <CreateAgentDialog toggle={toggle} />}
      </ProtectedComponent>
    </>
  );
}
